import React, { useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { storeAdTagDomains } from '@/store/adTagDomains';
import { MAX_IMPORT_DOMAINS, parseDomainList } from '@/utils/parseDomainList';

type Props = {
    open: boolean;
    onClose: () => void;
};

type Step = 'input' | 'preview' | 'result';

/** Экранирование формул — защита от CSV-инъекций при выгрузке ошибок */
function csvCell(value: string): string {
    const dangerous = /^[=+\-@]/;
    const safe = dangerous.test(value) ? `'${value}` : value;

    return `"${safe.replace(/"/g, '""')}"`;
}

function downloadErrors(rows: Array<{ value: string; reason: string }>) {
    const csv = ['Значение;Причина']
        .concat(rows.map((row) => `${csvCell(row.value)};${csvCell(row.reason)}`))
        .join('\n');

    // BOM — чтобы Excel открыл UTF-8 корректно
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'adtag-import-errors.csv';
    link.click();

    URL.revokeObjectURL(url);
}

/**
 * Диалог массового добавления доменов (T-13).
 *
 * Три шага: ввод (список или файл) → предпросмотр → импорт с прогрессом.
 * Предпросмотр обязателен: админ должен видеть, сколько строк не пройдёт
 * валидацию, ДО записи — иначе «загрузил 100k, половина не применилась».
 */
const DomainImportDialog = observer(({ open, onClose }: Props) => {
    const store = storeAdTagDomains;
    const [tab, setTab] = useState(0);
    const [rawText, setRawText] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const parsed = useMemo(() => parseDomainList(rawText), [rawText]);
    const overLimit = parsed.length > MAX_IMPORT_DOMAINS;
    const step: Step = store.importing || store.importResult
        ? (store.importing ? 'result' : 'result')
        : (store.preview ? 'preview' : 'input');

    const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => setRawText(String(reader.result ?? ''));
        reader.readAsText(file);
        event.target.value = '';
    };

    const handleClose = () => {
        setRawText('');
        setTab(0);
        store.resetImport();
        onClose();
    };

    const handlePreview = () => {
        void store.previewDomains(parsed);
    };

    const handleImport = () => {
        void store.importDomains(parsed);
    };

    const resetToInput = () => {
        store.resetImport();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>Добавить домены</DialogTitle>

            <DialogContent dividers>
                {step === 'input' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
                            <Tab label="Вставить списком" />
                            <Tab label="Загрузить файл" />
                        </Tabs>

                        {tab === 0 ? (
                            <TextField
                                multiline
                                minRows={10}
                                maxRows={18}
                                fullWidth
                                value={rawText}
                                onChange={(e) => setRawText(e.target.value)}
                                placeholder={'example.com\nsite.ru\nhttps://another.com/path'}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".txt,.csv,text/plain,text/csv"
                                    style={{ display: 'none' }}
                                    onChange={handleFile}
                                />
                                <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                                    Выбрать файл (.txt, .csv)
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    Файл разбирается в браузере и на сервер не передаётся.
                                    Разделители — перенос строки, запятая, точка с запятой.
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip label={`Строк: ${parsed.length.toLocaleString('ru-RU')}`} size="small" />
                            {overLimit && (
                                <Typography color="error" variant="body2">
                                    Превышен лимит импорта — максимум{' '}
                                    {MAX_IMPORT_DOMAINS.toLocaleString('ru-RU')} доменов за раз.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {step === 'preview' && store.preview && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1">Проверка списка</Typography>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={`Всего: ${store.preview.total.toLocaleString('ru-RU')}`} />
                            <Chip color="success" label={`Будет добавлено: ${store.preview.valid.toLocaleString('ru-RU')}`} />
                            <Chip label={`Уже есть: ${store.preview.existing.toLocaleString('ru-RU')}`} />
                            <Chip label={`Повторов в списке: ${store.preview.duplicatesInInput.toLocaleString('ru-RU')}`} />
                            <Chip
                                color={store.preview.invalidCount > 0 ? 'error' : 'default'}
                                label={`Ошибок: ${store.preview.invalidCount.toLocaleString('ru-RU')}`}
                            />
                        </Box>

                        {store.preview.invalid.length > 0 && (
                            <>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">
                                        Примеры ошибок (первые {store.preview.invalid.length})
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={() => downloadErrors(store.preview?.invalid ?? [])}
                                    >
                                        Скачать список ошибок
                                    </Button>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Значение</TableCell>
                                            <TableCell>Причина</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {store.preview.invalid.slice(0, 20).map((row, index) => (
                                            <TableRow key={`${row.index}-${index}`}>
                                                <TableCell>{row.value}</TableCell>
                                                <TableCell>{row.reason}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </>
                        )}
                    </Box>
                )}

                {step === 'result' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="subtitle1">
                            {store.importing ? 'Импорт…' : 'Импорт завершён'}
                        </Typography>

                        <LinearProgress
                            variant={store.importing ? 'determinate' : 'determinate'}
                            value={store.importProgressPercent}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Обработано {store.processed.toLocaleString('ru-RU')} из{' '}
                            {store.queued.toLocaleString('ru-RU')}
                        </Typography>

                        {store.importResult && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip color="success" label={`Добавлено: ${store.importResult.created.toLocaleString('ru-RU')}`} />
                                <Chip label={`Уже есть: ${store.importResult.skippedExisting.toLocaleString('ru-RU')}`} />
                                <Chip
                                    color={store.importResult.invalidCount > 0 ? 'error' : 'default'}
                                    label={`Ошибок: ${store.importResult.invalidCount.toLocaleString('ru-RU')}`}
                                />
                            </Box>
                        )}

                        {store.importError && <Alert severity="warning">{store.importError}</Alert>}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {step === 'input' && (
                    <>
                        <Button onClick={handleClose}>Отмена</Button>
                        <Button
                            variant="contained"
                            disabled={parsed.length === 0 || overLimit || store.previewLoading}
                            onClick={handlePreview}
                        >
                            {store.previewLoading ? 'Проверка…' : 'Проверить'}
                        </Button>
                    </>
                )}

                {step === 'preview' && (
                    <>
                        <Button onClick={resetToInput}>Назад</Button>
                        <Button
                            variant="contained"
                            disabled={!store.preview || store.preview.valid === 0}
                            onClick={handleImport}
                        >
                            Импортировать
                        </Button>
                    </>
                )}

                {step === 'result' && (
                    <Button variant="contained" onClick={handleClose} disabled={store.importing}>
                        Закрыть
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
});

export default DomainImportDialog;
